/**
 * Heatmap answers as metadata sends them. The schemas are generated from metadata's
 * openapi.yaml (`npm run generate`), and the types come from the schemas.
 */

import { z } from 'zod';

import { listOf } from '@/api/listOf';

import {
    HeatmapRecord as HeatmapRecordSchema, TimePeriod as TimePeriodSchema,
} from './generated/metadata.zod';
import type { NormalizedRect } from './player';
import type { LoadState } from './snapshot';

/** A run of continuous activity, as `/heatmap/segments` or `/events/segments` merges one. */
export type TimePeriod = z.infer<typeof TimePeriodSchema>;

/** What the segments endpoints answer while `flat=false`, which the transport always asks. */
export const HeatmapSegmentsSchema = z.object({
    periods: listOf(TimePeriodSchema, 'periods'),
});
export type HeatmapSegments = z.infer<typeof HeatmapSegmentsSchema>;

/** One ~1s slice of heatmap, and where in the frame it saw motion. */
export { HeatmapRecordSchema };
export type HeatmapRecord = z.infer<typeof HeatmapRecordSchema>;

/** One segments query: the resolution and the window the lane or the list paints. */
export interface MetadataSegmentsOpts {
    category: string;
    intervalMs: number;
    utcTimeMs?: number;
    utcEndTimeMs?: number;
    /** As `getBoundingBoxParam` writes it. */
    boundingBox?: string;
}

/** One page of heatmap rows over a window; the server caps `limit`. */
export interface HeatmapRecordsOpts {
    category: string;
    utcTimeMs: number;
    utcEndTimeMs: number;
    limit: number;
}

/** The endpoint that answers the periods of a search kind. */
export enum SegmentsApi {
    Events = 'events',
    Heatmap = 'heatmap',
}

/** One segments query, as the timeline lane or the motion list builds it. */
export interface MetadataSegmentsRequest {
    segmentsApi: SegmentsApi;
    cameraId: string;
    category: string;
    intervalMs: number;
    /** `null` is no bound. */
    startMs: number | null;
    endMs: number | null;
    /** As `getBoundingBoxParam` writes it; `null` is the whole frame. */
    boundingBox: string | null;
}

export interface MetadataSegmentsSnapshot extends LoadState {
    /** The server's list, as it came. */
    periods: TimePeriod[];
    /** True from the first load that succeeded. */
    isLoaded: boolean;
}

/** The motion period that a card shows its motion box for. */
export interface MotionBoxRequest {
    cameraId: string;
    startMs: number;
    endMs: number;
}

export interface MotionBoxSnapshot extends LoadState {
    /** Null until the rows answered, and when no row had motion. */
    box: NormalizedRect | null;
}
